/**
 * BED FORM LINK DIALOG
 * Émission du lien du FORMULAIRE BED depuis la fiche équipage (migration 00178).
 *
 * ⚠️ À ne pas confondre avec `BedLinkDialog` (`src/components/payment/`), qui
 * émet le lien de PAIEMENT direct du solde. Ici on émet le lien du BULLETIN :
 * l'URL publique du formulaire, portant le `bed_access_token` de l'équipage.
 * C'est ce lien que le participant ouvre pour remplir son BED — et dont la
 * première ouverture fait passer l'équipage en « BED téléchargé ».
 *
 * Modèle d'UX repris de `BedLinkDialog` : saisie minimale → génération →
 * écran « lien prêt » avec copie.
 */

import { useEffect, useState } from 'react';
import { Copy, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, toast } from '../ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { bedAdminErrorMessage, mintBedFormLink } from '../../lib/bed/admin';

// Durées de validité proposées. Le défaut (60 j) est celui de
// `create_bed_access_token` (00176).
const EXPIRY_OPTIONS = [
  { value: '30', label: '30 jours' },
  { value: '60', label: '60 jours' },
  { value: '90', label: '90 jours' },
  { value: '180', label: '180 jours' },
];

interface BedFormLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  crewId: string;
  crewName: string;
  /** Rafraîchit la zone BED à la fermeture (nouveau lien courant, timeline). */
  onGenerated: () => void;
}

export function BedFormLinkDialog({
  open,
  onOpenChange,
  crewId,
  crewName,
  onGenerated,
}: BedFormLinkDialogProps) {
  const [expiresInDays, setExpiresInDays] = useState('60');
  const [submitting, setSubmitting] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);

  // Le dialog reste monté : on réinitialise à chaque ouverture.
  useEffect(() => {
    if (!open) return;
    setGeneratedLink(null);
    setExpiresInDays('60');
  }, [open]);

  async function handleGenerate() {
    setSubmitting(true);
    const result = await mintBedFormLink(crewId, Number(expiresInDays));
    if (result.status === 'ok') {
      setGeneratedLink(result.url);
      toast.success('Lien du formulaire BED généré');
    } else {
      toast.error(
        result.status === 'error'
          ? bedAdminErrorMessage('error')
          : bedAdminErrorMessage(result.status)
      );
    }
    setSubmitting(false);
  }

  async function handleCopy() {
    if (!generatedLink) return;
    try {
      await navigator.clipboard.writeText(generatedLink);
      toast.success('Lien copié dans le presse-papiers');
    } catch {
      toast.error('Impossible de copier le lien.');
    }
  }

  function handleClose(value: boolean) {
    if (submitting) return;
    onOpenChange(value);
    // Refresh différé à la fermeture : l'écran « lien prêt » doit rester
    // utilisable jusqu'à la copie (même raison que BedLinkDialog).
    if (!value && generatedLink) onGenerated();
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <DialogTitle>Générer le lien du formulaire BED</DialogTitle>
          <DialogDescription>
            Bulletin d'engagement définitif — <strong>{crewName}</strong>
          </DialogDescription>
        </DialogHeader>

        {generatedLink ? (
          <div className="flex flex-col gap-4">
            <div className="rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-800">
              Lien du <strong>formulaire</strong> à transmettre au participant. À sa première
              ouverture, l'équipage passera automatiquement en « BED téléchargé ».
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-zinc-700">Lien du bulletin</span>
              <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 font-mono text-xs break-all text-zinc-700">
                {generatedLink}
              </div>
            </div>
            <Button variant="outline" onClick={handleCopy} className="justify-center">
              <Copy className="mr-2 size-4" />
              Copier le lien
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-zinc-700" htmlFor="bed-form-link-expiry">
                Durée de validité
              </label>
              <Select
                id="bed-form-link-expiry"
                value={expiresInDays}
                options={EXPIRY_OPTIONS}
                disabled={submitting}
                onChange={(e) => setExpiresInDays(e.target.value)}
              />
            </div>
            <p className="text-xs text-zinc-400">
              Le lien identifie l'équipage et pré-remplit le bulletin avec les données du CRM. Il
              exige un BPI déjà payé.
            </p>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(false)}
                disabled={submitting}
              >
                Annuler
              </Button>
              <Button
                type="button"
                onClick={handleGenerate}
                disabled={submitting}
                className="bg-teal-600 hover:bg-teal-700 text-white"
              >
                <FileText className="mr-2 size-4" />
                {submitting ? 'Génération...' : 'Générer le lien'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
